/**
 * Componentes de campo reutilizables para formularios de opinión
 * Estos componentes abstraen los campos comunes entre OpinionForm y SorteoOpinionForm
 */
import React from "react";

/**
 * Campo de radio buttons horizontal
 */
export const RadioGroup = ({
    label,
    name,
    options,
    register,
    required = true,
    error,
    errorMessage = "Campo obligatorio",
    className = "",
    labelClassName = "text-left block font-semibold",
    optionClassName = "text-gray-700 dark:text-gray-300",
}) => (
    <div className={`mb-4 ${className}`}>
        <label className={labelClassName}>{label}</label>
        <div className="justify-center flex gap-4 flex-wrap">
            {options.map((opt) => {
                const value = typeof opt === "string" ? opt : opt.value;
                const displayLabel = typeof opt === "string" ? opt : opt.label;
                return (
                    <label key={value} className={optionClassName}>
                        <input
                            type="radio"
                            value={value}
                            {...register(name, { required })}
                        />{" "}
                        {displayLabel}
                    </label>
                );
            })}
        </div>
        {error && <span className="text-red-500 text-sm">{errorMessage}</span>}
    </div>
);

/**
 * Campo de escala numérica (1-5)
 */
export const ScaleField = ({
    label,
    description,
    name,
    register,
    required = true,
    error,
    errorMessage = "Campo obligatorio",
    className = "",
    labelClassName = "text-left block font-semibold",
    descriptionClassName = "text-sm text-gray-600 dark:text-gray-400 mb-2",
    optionClassName = "text-gray-700 dark:text-gray-300",
    min = 1,
    max = 5,
}) => (
    <div className={`mb-4 ${className}`}>
        <label className={labelClassName}>{label}</label>
        {description && <p className={descriptionClassName}>{description}</p>}
        <div className="flex justify-between mt-2 max-w-xs mx-auto">
            {Array.from({ length: max - min + 1 }, (_, i) => min + i).map((value) => (
                <label key={value} className={`flex flex-col items-center ${optionClassName}`}>
                    <input
                        type="radio"
                        value={value}
                        {...register(name, { required })}
                        className="form-radio"
                    />
                    <span className="mt-1 text-sm">{value}</span>
                </label>
            ))}
        </div>
        {error && <span className="text-red-500 text-sm">{errorMessage}</span>}
    </div>
);

/**
 * Campo de texto/número
 */
export const TextField = ({
    label,
    name,
    type = "text",
    register,
    validation = {},
    error,
    errorMessage = "Campo obligatorio",
    value,
    onChange,
    placeholder = "",
    helpText = "",
    className = "",
    labelClassName = "text-left block font-semibold",
    inputClassName = "border rounded-md p-2 w-full",
    ...inputProps
}) => (
    <div className={`mb-4 ${className}`}>
        <label className={labelClassName}>{label}</label>
        <input
            type={type}
            {...register(name, validation)}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            className={inputClassName}
            {...inputProps}
        />
        {helpText && <p className="text-xs text-gray-500 mt-1">{helpText}</p>}
        {error && <span className="text-red-500 text-sm">{errorMessage}</span>}
    </div>
);

/**
 * Campo de textarea
 */
export const TextAreaField = ({
    label,
    name,
    register,
    validation = {},
    className = "",
    labelClassName = "text-left block font-semibold",
    inputClassName = "border rounded-md p-2 w-full",
    ...textareaProps
}) => (
    <div className={`mb-4 ${className}`}>
        <label className={labelClassName}>{label}</label>
        <textarea
            {...register(name, validation)}
            className={inputClassName}
            {...textareaProps}
        />
    </div>
);

/**
 * Campo de checkbox (GDPR, términos, etc.)
 */
export const CheckboxField = ({
    label,
    name,
    register,
    required = true,
    error,
    errorMessage = "Debes aceptar para continuar",
    className = "",
    labelClassName = "flex items-center",
}) => (
    <div className={`mb-4 ${className}`}>
        <label className={labelClassName}>
            <input
                type="checkbox"
                {...register(name, { required })}
                className="form-checkbox h-5 w-5 text-yellow-500 mr-2"
            />
            <span className="text-sm">{label}</span>
        </label>
        {error && <span className="text-red-500 text-sm block">{errorMessage}</span>}
    </div>
);

/**
 * Grupo de emociones (fieldset con múltiples escalas)
 */
export const EmotionsFieldset = ({
    legend,
    emotions,
    register,
    errors,
    labelClassName = "text-lg font-semibold",
    className = "",
}) => (
    <fieldset className={`mb-4 border rounded-md p-4 ${className}`}>
        <legend className={labelClassName}>{legend}</legend>
        {emotions.map((emotion) => (
            <div key={emotion.name} className="mt-4">
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                    {emotion.description}
                </p>
                <div className="flex justify-between mt-2 max-w-xs mx-auto">
                    {[1, 2, 3, 4, 5].map((v) => (
                        <label key={v} className="flex flex-col items-center">
                            <input
                                type="radio"
                                value={v}
                                {...register(emotion.name, { required: true })}
                                className="form-radio text-yellow-500"
                            />
                            <span className="mt-1 text-sm">{v}</span>
                        </label>
                    ))}
                </div>
                {errors[emotion.name] && (
                    <span className="text-red-500 text-sm">Campo obligatorio</span>
                )}
            </div>
        ))}
    </fieldset>
);

/**
 * Selector de capítulo
 */
export const ChapterSelector = ({
    label,
    name,
    register,
    required = true,
    error,
    errorMessage = "Selecciona un capítulo",
    chapters = [1, 2, 3, 4, 5, 6, 7, 8, 9],
    className = "",
    labelClassName = "text-left block font-semibold",
    optionClassName = "",
}) => (
    <div className={`mb-4 ${className}`}>
        <label className={labelClassName}>{label}</label>
        <div className="justify-center flex gap-4 flex-wrap">
            {chapters.slice(0, 5).map((n) => (
                <label key={n} className={optionClassName}>
                    <input
                        type="radio"
                        value={`Capítulo ${n}`}
                        {...register(name, { required })}
                    />{" "}
                    Capítulo {n}
                </label>
            ))}
        </div>
        {chapters.length > 5 && (
            <div className="justify-center flex gap-4 flex-wrap mt-2">
                {chapters.slice(5).map((n) => (
                    <label key={n} className={optionClassName}>
                        <input
                            type="radio"
                            value={`Capítulo ${n}`}
                            {...register(name, { required })}
                        />{" "}
                        Capítulo {n}
                    </label>
                ))}
            </div>
        )}
        {error && <span className="text-red-500 text-sm">{errorMessage}</span>}
    </div>
);
